import { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, ArrowRight, Database, FileText } from 'lucide-react';
import { Button } from '@components/common/Button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@components/common/Dialog';
import type { ErpRealmSwitchOptions, ErpRealmSwitchResponse } from '@schemas/api';
import { InfoTooltip } from '@components/common/InfoTooltip';

interface ErpRealmSwitchModalProps {
  open: boolean;
  realmSwitch: ErpRealmSwitchResponse | null;
  isExecuting: boolean;
  isDismissing: boolean;
  onOpenChange: (open: boolean) => void;
  onExecute: (options: ErpRealmSwitchOptions) => void;
  onDismiss: () => void;
}

const defaultOptions: ErpRealmSwitchOptions = {
  sync_vendors: true,
  sync_items: true,
  sync_accounts: true,
  sync_customers: true,
  sync_ap_posting_bills: true,
};

const formatCount = (value?: number) =>
  typeof value === 'number' ? value.toLocaleString() : '0';

interface RealmSwitchRow {
  key: keyof ErpRealmSwitchOptions;
  label: string;
  count?: number;
  detail: string | null;
  disabled?: boolean;
}

export const ErpRealmSwitchModal = ({
  open,
  realmSwitch,
  isExecuting,
  isDismissing,
  onOpenChange,
  onExecute,
  onDismiss,
}: ErpRealmSwitchModalProps) => {
  const [options, setOptions] = useState<ErpRealmSwitchOptions>(defaultOptions);

  useEffect(() => {
    if (realmSwitch?.id) {
      setOptions({ ...defaultOptions, ...realmSwitch.sync_options });
    }
  }, [realmSwitch?.id, realmSwitch?.sync_options]);

  const apPostingDependenciesSelected = useMemo(
    () => options.sync_vendors && options.sync_items && options.sync_accounts && options.sync_customers,
    [options.sync_accounts, options.sync_customers, options.sync_items, options.sync_vendors]
  );

  useEffect(() => {
    if (!apPostingDependenciesSelected && options.sync_ap_posting_bills) {
      setOptions((current) => ({ ...current, sync_ap_posting_bills: false }));
    }
  }, [apPostingDependenciesSelected, options.sync_ap_posting_bills]);

  if (!realmSwitch) {
    return null;
  }

  const preview = realmSwitch.preview ?? {};
  const apBillCount =
    (preview.ap_posting_bills?.open_total ?? 0) +
    (preview.ap_posting_bills?.posted_total ?? 0);

  const rows: RealmSwitchRow[] = [
    {
      key: 'sync_vendors',
      label: 'Vendors',
      count: preview.vendors?.total,
      detail: null,
    },
    {
      key: 'sync_items',
      label: 'Items',
      count: preview.items?.total,
      detail: `${formatCount(preview.items?.auto_matchable)} matched`,
    },
    {
      key: 'sync_accounts',
      label: 'Accounts',
      count: preview.accounts?.total,
      detail: `${formatCount(preview.accounts?.auto_matchable)} matched`,
    },
    {
      key: 'sync_customers',
      label: 'Customers',
      count: preview.customers?.total,
      detail: `${formatCount(preview.customers?.auto_matchable)} matched`,
    },
    {
      key: 'sync_ap_posting_bills',
      label: 'AP posting bills',
      count: apBillCount,
      detail: 'Requires all records',
      disabled: !apPostingDependenciesSelected,
    },
  ];

  const updateOption = (key: keyof ErpRealmSwitchOptions, value: boolean) => {
    setOptions((current) => ({
      ...current,
      [key]: value,
      sync_ap_posting_bills:
        key !== 'sync_ap_posting_bills' && !value
          ? false
          : current.sync_ap_posting_bills,
    }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange} className="max-w-2xl">
      <DialogContent className="space-y-5">
        <DialogHeader>
          <div className="flex items-start gap-3">
            <div className="mt-1 rounded-md bg-amber-50 p-2 text-amber-700">
              <AlertTriangle className="h-5 w-5" />
            </div>
            <div className="flex items-center gap-2">
              <DialogTitle className="text-xl text-gray-900">Switch QuickBooks Realm</DialogTitle>
              <InfoTooltip>Choose which local records to prepare for the new connection.</InfoTooltip>
            </div>
          </div>
        </DialogHeader>

        <div className="grid gap-2 rounded-md border border-gray-200 bg-gray-50 p-3 text-sm sm:grid-cols-[1fr_auto_1fr]">
          <div className="min-w-0">
            <div className="text-xs font-medium uppercase text-gray-500">Current</div>
            <div className="truncate font-mono text-gray-900">{realmSwitch.old_realm_id}</div>
          </div>
          <ArrowRight className="hidden h-4 w-4 self-center text-gray-400 sm:block" />
          <div className="min-w-0">
            <div className="text-xs font-medium uppercase text-gray-500">New</div>
            <div className="truncate font-mono text-gray-900">{realmSwitch.new_realm_id}</div>
          </div>
        </div>

        <div className="divide-y divide-gray-200 rounded-md border border-gray-200">
          {rows.map((row) => (
            <label
              key={row.key}
              className={`flex items-center justify-between gap-4 px-4 py-3 ${
                row.disabled ? 'bg-gray-50 text-gray-400' : 'bg-white text-gray-900'
              }`}
            >
              <span className="flex min-w-0 items-center gap-3">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-gray-300 text-primary-600"
                  checked={options[row.key]}
                  disabled={row.disabled || isExecuting || isDismissing}
                  onChange={(event) => updateOption(row.key, event.target.checked)}
                />
                {row.key === 'sync_ap_posting_bills' ? (
                  <FileText className="h-4 w-4 shrink-0 text-gray-500" />
                ) : (
                  <Database className="h-4 w-4 shrink-0 text-gray-500" />
                )}
                <span className="min-w-0">
                  <span className="block truncate text-sm font-medium">{row.label}</span>
                  {row.detail ? (
                    <span className="block text-xs text-gray-500">{row.detail}</span>
                  ) : null}
                </span>
              </span>
              <span className="shrink-0 text-sm font-semibold">{formatCount(row.count)}</span>
            </label>
          ))}
        </div>

        <div className="flex flex-col-reverse gap-2 border-t border-gray-200 pt-4 sm:flex-row sm:justify-end">
          <Button
            variant="secondary"
            onClick={onDismiss}
            disabled={isExecuting || isDismissing}
            loading={isDismissing}
          >
            Not Now
          </Button>
          <Button
            variant="primary"
            onClick={() => onExecute(options)}
            disabled={isExecuting || isDismissing}
            loading={isExecuting}
          >
            Sync Records and Switch
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
